import { Helmet } from "react-helmet-async";
import {
  SITE_URL,
  SITE_NAME,
  ROUTES_SEO,
  resolveOgImage,
} from "@/seo/routes";

interface SEOProps {
  title: string;
  description: string;
  /** Path beginning with "/" — used to build canonical URL and pick OG image */
  path?: string;
  /** Optional override for the OG / Twitter image. Falls back to per-route asset, then global default. */
  image?: string;
  /** One or more JSON-LD schema objects to embed in the head */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Defaults to "index, follow, max-image-preview:large, max-snippet:-1" */
  robots?: string;
  /** Set to true on legal/secondary pages where indexing is fine but lower priority */
  noindex?: boolean;
}

/**
 * Per-route SEO component. Sets title, meta description, canonical, OpenGraph,
 * Twitter Card and optional JSON-LD structured data.
 *
 * NOTE: For social media scrapers (LinkedIn, Twitter, Facebook, Slack) and
 * non-JS crawlers, the *static* HTML files generated by
 * `scripts/build-seo-html.mjs` are what actually matters. This runtime
 * component updates the head for users who navigate client-side and for
 * JS-capable crawlers (Googlebot rendering pass).
 *
 * Source of truth for per-route metadata is `src/seo/routes.ts` — keep both
 * in sync. The build script reads that file to generate the static HTML.
 */
export const SEO = ({
  title,
  description,
  path = "/",
  image,
  jsonLd,
  robots,
  noindex,
}: SEOProps) => {
  const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
  const ogImage = resolveOgImage(path, image);
  const robotsContent =
    robots ||
    (noindex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1");

  const schemas = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* OpenGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

/** Helper builders for common JSON-LD blocks */
export const buildBreadcrumbSchema = (
  items: { name: string; path: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: `${SITE_URL}${item.path === "/" ? "" : item.path}`,
  })),
});

export const buildFAQSchema = (
  faqs: { question: string; answer: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.answer,
    },
  })),
});

/** Re-export so existing imports of ROUTES_SEO from @/seo/routes still work */
export { ROUTES_SEO };
